import { PropsWithChildren, useState } from "react";
import { makeStyles } from "@fluentui/react-components";
import { IApiError } from "@interfaces/common.interfaces";
import { getError } from "lib/utils";
import ApiErrorBar from "components/common/api-error-bar/api-error-bar";
import { IFormProps } from "./form.props";
import { useForm, FormProvider, FieldValues } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { usePPA } from "context/ppa-context";
import FormButtons from "../form-buttons/form-buttons";
import LoadingSpinner from "components/common/loading-spinner/loading-spinner";

const layoutStyles = makeStyles({
    mainWrapper: {
        padding: '0 1em',
        maxWidth: '900px'
    }
});

export default function Form<T extends FieldValues>(props: PropsWithChildren<IFormProps<T>>) {
    const styles = layoutStyles();
    const { getApiClient } = usePPA();
    const [apiError, setApiError] = useState<IApiError | null>(null);
    const [loadingApiError, setLoadingApiError] = useState<IApiError | null>(null);

    const getExisting = async () => {
        setLoadingApiError(null);
        return new Promise<T | null>((resolve) => {
            if (!props.getExistingApiUrl) {
                resolve(props.defaults);
                return;
            }

            getApiClient().get<T>(props.getExistingApiUrl)
                .then(
                    (response) => {
                        resolve(response.data);
                    },
                    (error) => {
                        setLoadingApiError(getError(error));
                        resolve(null);
                    });
        });
    };

    const methods = useForm({
        resolver: yupResolver(props.schema),
        defaultValues: getExisting
    });

    const getApiMethod = () => {
        switch (props.method) {
            case "PUT":
                return getApiClient().put<T>;
            default:
                return getApiClient().post<T>;
        }
    }

    const onSubmit = async (data: FieldValues) => {

        // Remove the model properties that do not exist in the validation schema.
        Object.keys(data).forEach((prop: string) => {
            if (Object.keys(props.schema.fields).find(f => f === prop) === undefined) {
                data[prop] = null;
            }
        });

        setApiError(null);
        return new Promise<T>((resolve) => {
            getApiMethod()(props.apiUrl, data)
                .then(
                    (response) => {
                        resolve(response.data);
                        props.onSuccess();
                    },
                    (error) => {
                        setApiError(getError(error));
                        resolve(error);
                    });
        });
    };

    return (
        <div className={styles.mainWrapper}>
            <FormProvider {...methods}>
                <LoadingSpinner loading={methods.formState.isLoading} />
                {loadingApiError &&
                    <ApiErrorBar error={loadingApiError} />}
                {!loadingApiError &&
                    <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
                        {!methods.formState.isLoading && props.children}
                        <FormButtons
                            apiError={apiError}
                            submitLabel={props.submitLabel}
                            submittingLabel={props.submittingLabel}
                            onCancel={props.onCancel}
                            cancelLabel={props.cancelLabel} />
                        <ApiErrorBar error={apiError} />
                    </form>}
            </FormProvider>
        </div>
    );
}